<template>
  <div style="margin:20px">
    <FiPageRail title="Actuals - Driver Daily Report"/>
    <div style="position: relative; font-weight: bold; font-size: 24px" class="align-right d-flex align-items-center justify-content-center">
      <a href="#" class="arrows" v-on:click.prevent="shift_detail_graph_date(-1)"><</a>
      &nbsp;&nbsp;<date-picker style="font-size: 20px; font-weight: bold; width:140px" v-model="details_fromDate" :config="options" :placeholder="details_fromDate"></date-picker>&nbsp;&nbsp;
      <a href="#" class="arrows" v-on:click.prevent="shift_detail_graph_date(1)">></a>
      &nbsp;&nbsp;|&nbsp;&nbsp;
      Use Booking Actuals:&nbsp;&nbsp;<b-checkbox size="lg" v-model="use_booking_actuals"></b-checkbox>
      &nbsp;&nbsp;|&nbsp;&nbsp;
      <b-button v-on:click.prevent="load_data(false)" :disabled="loading">Get Results</b-button>
    </div>
    <div v-if="results_cached_at" class="text-center mt-1" style="font-size: 14px; color: #888;">
      Using data from {{ formattedCachedAt }}
      — <a href="#" @click.prevent="load_data(true)" style="font-size: 14px;">Pull New Data</a>
    </div>

    <div v-if="polling" class="mt-3 p-3 border rounded bg-light d-flex align-items-center justify-content-center">
      <b-spinner style="width: 3rem; height: 3rem;" class="mr-3" label="Loading..."></b-spinner>
      <span>
        <template v-if="!email_opt_in_sent">
          Report is still generating. Stay put, or <a href="#" @click.prevent="optInEmailWhenReady">get an in-app notification when it's done</a>.
        </template>
        <template v-else>
          Report is still generating. You'll get an in-app notification when it's done.
        </template>
      </span>
    </div>

    <div v-if="!loading">
      <div class="row" style="margin:30px; font-weight: bold; font-size: 22px;">
        <div v-if="summary.total_cnt" class="col" style="border: 1px solid grey;">{{items.length}} of {{summary.total_cnt}}</div>
        <div v-if="summary.total_cnt && summary.payroll_enabled" class="col" style="border: 1px solid grey;">Planned-Payroll&nbsp;&nbsp;<b-form-select v-model="plan_payroll_threshold" :options="duration_threshold_options" style="font-size: 20px; font-weight: bold; width:110px"/></div>
        <div v-if="summary.total_cnt && summary.payroll_enabled" class="col" style="border: 1px solid grey;">HOS-Payroll&nbsp;&nbsp;<b-form-select v-model="hos_payroll_threshold" :options="duration_threshold_options" style="font-size: 20px; font-weight: bold; width:110px"/></div>
        <div v-else-if="summary.total_cnt && !summary.payroll_enabled" class="col" style="border: 1px solid grey;">HOS-Planned&nbsp;&nbsp;<b-form-select v-model="hos_planned_threshold" :options="duration_threshold_options" style="font-size: 20px; font-weight: bold; width:110px"/></div>
        <div v-if="summary.total_cnt" class="col align-right d-flex align-items-center justify-content-center" style="border: 1px solid grey;">Filter&nbsp;<b-form-input style="width: 200px" v-model="booking_filter" placeholder="charterID,vehicle,driver"/></div>
        <div v-if="summary.event_categories" class="col align-right d-flex align-items-center justify-content-center" style="border: 1px solid grey;">Category&nbsp;<b-form-select v-model="category_filter" :options="summary.event_categories_options" style="font-size: 20px; font-weight: bold; width:110px"/></div>
        <div v-if="summary.total_cnt" class="col align-right d-flex align-items-center justify-content-center" style="border: 1px solid grey;"><b-button size="md" variant="success" @click="download">Download Report</b-button></div>
      </div>
      <div class="row" style="margin:30px; font-weight: bold; font-size: 24px;">
        <div v-if="summary.hosHours" class="col" style="border: 1px solid grey;">Total HOS Hours: {{summary.hosHours}}</div>
        <div v-if="summary.payrollHours" class="col" style="border: 1px solid grey;">Total Payroll Hours: {{summary.payrollHours}}</div>
        <div v-if="summary.hosPercentUtilized" class="col" style="border: 1px solid grey;">{{summary.hosPercentUtilized}}% with HOS Logs</div>
        <div v-if="summary.payrollPercentUtilized" class="col" style="border: 1px solid grey;">{{summary.payrollPercentUtilized}}% with Payroll Records</div>
      </div>
    </div>

    <table v-if="!loading && items.length > 0" width="100%" border="1" cellpadding="2">
      <thead>
      <tr>
        <th colspan="7" style="font-size: 18px; font-weight: bold;">&nbsp;</th>
        <th colspan="3" style="background-color: #C6C6C6">Planned</th>
        <th colspan="3" style="background-color: #D5D5D5">HOS</th>
        <th v-if="summary.payroll_enabled" colspan="3" style="background-color: #E5E5E5">Payroll</th>
        <th style="background-color: #F5F5F5">Time Diffs</th>
        <th v-if="summary.payroll_enabled" style="background-color: #F5F5F5">Time Diffs</th>
        <th v-if="summary.event_categories" style="background-color: #F9F9F9">&nbsp;</th>
        <th colspan="2" v-if="summary.notes_enabled" style="background-color: #F9F9F9" >Notes</th>
      </tr>
      <tr class="stripe">
        <th width="10px"></th>
        <th @click="sort_strings('driver')">Driver</th>
        <th @click="sort_strings('vehicle')">Vehicle</th>
        <th @click="sort_numbers('charterID')">CharterID</th>
        <th @click="sort_strings('is_multiday')">Multiday</th>
        <th @click="sort_strings('start_base')">Start Base</th>
        <th @click="sort_strings('finish_base')">Finish Base</th>
        <th @click="sort_dates('plannedStartDateTime')">Start</th>
        <th @click="sort_dates('plannedFinishDateTime')">Finish</th>
        <th @click="sort_numbers('plannedMinutes')">Total</th>
        <th @click="sort_dates('actualHOSStartDateTime')">Start</th>
        <th @click="sort_dates('actualHOSFinishDateTime')">Finish</th>
        <th @click="sort_numbers('hosMinutes')">Total</th>
        <th v-if="summary.payroll_enabled" @click="sort_dates('actualPayrollStartDateTime')">Start</th>
        <th v-if="summary.payroll_enabled" @click="sort_dates('actualPayrollFinishDateTime')">Finish</th>
        <th v-if="summary.payroll_enabled" @click="sort_numbers('payrollMinutes')">Total</th>
        <th v-if="summary.payroll_enabled" @click="sort_numbers('plannedMinusPayroll')">Planned-Payroll Diff</th>
        <th v-else @click="sort_numbers('hosMinusPlanned')">HOS-Planned Diff</th>
        <th v-if="summary.payroll_enabled" @click="sort_numbers('hosMinusPayroll')">HOS-Payroll Diff</th>
        <th v-if="summary.event_categories">Categories</th>
        <th v-if="summary.notes_enabled" width="200px" @click="sort_strings('payrollNotes')">Payroll Notes</th>
        <th v-if="summary.notes_enabled" width="200px" @click="sort_strings('notes')">Notes</th>
      </tr>
      </thead>
      <template v-for="item in items">
        <tr class="stripe">
          <td><b-button size="sm" @click="toggle_div(item,'isDetailsVisible')">{{ item.isDetailsVisible ? '-' : '+' }}</b-button></td>
          <td>{{ item.driver }}<a target="_blank" :href="item.driver_hos_link"><img style="margin: 0px 5px 5px 5px" src="/driver-icon.png"/></a></td>
          <td>{{ item.vehicle | trim(25) }}</td>
          <td>{{ item.charterID | trim(20) }}</td>
          <td>{{ item.is_multiday }}</td>
          <td>{{ item.start_base | trim(25)}}</td>
          <td>{{ item.finish_base | trim(25)}}</td>
          <td>{{item.plannedStartDateTime | formatDateTimeSmall(item.timezone)}}</td>
          <td>{{item.plannedFinishDateTime | formatDateTimeSmall(item.timezone)}}</td>
          <td>{{item.plannedMinutes | units('m')}}</td>
          <td>{{item.actualHOSStartDateTime | formatDateTimeSmall(item.timezone)}}</td>
          <td>{{item.actualHOSFinishDateTime | formatDateTimeSmall(item.timezone)}}</td>
          <td>{{item.hosMinutes | units('m')}}</td>
          <td v-if="summary.payroll_enabled">{{item.actualPayrollStartDateTime | formatDateTimeSmall(item.timezone)}}</td>
          <td v-if="summary.payroll_enabled">{{item.actualPayrollFinishDateTime | formatDateTimeSmall(item.timezone)}}</td>
          <td v-if="summary.payroll_enabled">{{item.payrollMinutes | units('m')}}</td>
          <td v-if="summary.payroll_enabled">{{item.plannedMinusPayroll | units('m')}}</td>
          <td v-else >{{item.hosMinusPlanned  | units('m')}}</td>
          <td v-if="summary.payroll_enabled">{{item.hosMinusPayroll | units('m')}}</td>
          <td width="250px" v-if="summary.event_categories" >
            <b-form-checkbox-group v-model="item.event_categories" v-on:input="save_notes(item)">
              &nbsp;<b-form-checkbox :value="category" v-for="category in summary.event_categories" :disabled="user.role=='manager'">{{category}}</b-form-checkbox>
            </b-form-checkbox-group>
          </td>
          <td v-if="summary.notes_enabled" align="left">{{item.payrollNotes}}</td>
          <td v-if="summary.notes_enabled" align="left"><b-form-input style="border: 1px solid black; min-width:200px" v-model="item.event_notes" v-on:blur="save_notes(item)" @keydown.enter.native="save_notes(item)" :disabled="user.role=='manager'"/></td>
        </tr>
        <tr style="border:2px solid var(--fi-rail-accent);" v-if="item.isDetailsVisible" v-for="nested in item.booking_results">
          <td>&nbsp;</td>
          <td style="font-weight: bold; color: white; text-align:right; background-color: gray">planned data</td>
          <td>{{ nested.vehicle | trim(25) }}</td>
          <td>{{ nested.charterID | trim(20) }}</td>
          <td>&nbsp;</td>
          <td>{{ nested.start_base }}</td>
          <td>{{ nested.finish_base }}</td>
          <td>{{nested.plannedStartDateTime | formatDateTimeSmall(nested.timezone)}}</td>
          <td>{{nested.plannedFinishDateTime | formatDateTimeSmall(nested.timezone)}}</td>
          <td>{{nested.plannedMinutes | units('m')}}</td>
          <td>{{nested.actualHOSStartDateTime | formatDateTimeSmall(nested.timezone)}}</td>
          <td>{{nested.actualHOSFinishDateTime | formatDateTimeSmall(nested.timezone)}}</td>
          <td>&nbsp;</td>
          <td v-if="summary.payroll_enabled">&nbsp;</td>
          <td v-if="summary.payroll_enabled">&nbsp;</td>
          <td v-if="summary.payroll_enabled">&nbsp;</td>
          <td v-if="summary.payroll_enabled">&nbsp;</td>
          <td>&nbsp;</td>
          <td v-if="summary.event_categories">&nbsp;</td>
          <td v-if="summary.notes_enabled">&nbsp;</td>
          <td v-if="summary.notes_enabled">&nbsp;</td>
        </tr>
        <tr style="border:2px solid var(--fi-rail-accent);" v-if="item.isDetailsVisible" v-for="nested in item.hos_results">
          <td>&nbsp;</td>
          <td style="font-weight: bold; color: white; text-align:right; background-color: gray">{{ nested.type }} data</td>
          <td>{{ nested.vehicle }}</td>
          <td>{{ nested.charterID }}</td>
          <td>&nbsp;</td>
          <td>&nbsp;</td>
          <td>&nbsp;</td>
          <td>&nbsp;</td>
          <td>&nbsp;</td>
          <td>&nbsp;</td>
          <td>{{nested.actualHOSStartDateTime | formatDateTimeSmall}}</td>
          <td>{{nested.actualHOSFinishDateTime | formatDateTimeSmall}}</td>
          <td>{{nested.hosMinutes}}</td>
          <td v-if="summary.payroll_enabled">&nbsp;</td>
          <td v-if="summary.payroll_enabled">&nbsp;</td>
          <td v-if="summary.payroll_enabled">&nbsp;</td>
          <td v-if="summary.payroll_enabled">&nbsp;</td>
          <td>&nbsp;</td>
          <td v-if="summary.event_categories">&nbsp;</td>
          <td v-if="summary.notes_enabled">&nbsp;</td>
          <td v-if="summary.notes_enabled">&nbsp;</td>
        </tr>
        <tr style="border:2px solid var(--fi-rail-accent);" v-if="item.isDetailsVisible" v-for="nested in item.payroll_results">
          <td>&nbsp;</td>
          <td style="font-weight: bold; color: white; text-align:right; background-color: gray">{{ nested.type }} data</td>
          <td>{{ nested.vehicle }}</td>
          <td>{{ nested.charterID }}</td>
          <td>&nbsp;</td>
          <td>&nbsp;</td>
          <td>&nbsp;</td>
          <td>&nbsp;</td>
          <td>&nbsp;</td>
          <td>&nbsp;</td>

          <td>&nbsp;</td>
          <td>&nbsp;</td>
          <td>&nbsp;</td>

          <td v-if="summary.payroll_enabled">{{nested.actualPayrollStartDateTime | formatDateTimeSmall}}</td>
          <td v-if="summary.payroll_enabled">{{nested.actualPayrollFinishDateTime | formatDateTimeSmall}}</td>
          <td v-if="summary.payroll_enabled">{{nested.payrollMinutes | units('m')}}</td>
          <td v-if="summary.payroll_enabled">&nbsp;</td>
          <td>&nbsp;</td>
          <td v-if="summary.event_categories">&nbsp;</td>
          <td v-if="summary.notes_enabled">{{nested.notes}}</td>
          <td v-if="summary.notes_enabled">&nbsp;</td>
        </tr>
      </template>
    </table>
    <div v-if="loading && !polling">
      <br/><br/>
      <b-spinner style="width: 3rem; height: 3rem;" label="Loading..."></b-spinner>
    </div>
  </div>

</template>
<script>
import {api_url, get_tbn_actuals_driver_payroll_report, tbn_register_email_when_ready} from '../networking/tbn_apis'
import {save_event_notes} from '../networking/apis'
import {mapState} from "vuex"
import {justDate} from '../helpers/dates'
import moment from 'moment'
import axios from "axios";

export default {
  name: 'actuals_driver_payroll_report_tbn',

  components: {
  },

  data() {
    return {
      booking_filter: '',
      items: [],
      items_full: [],
      summary: [],
      plan_payroll_threshold: null,
      hos_payroll_threshold: null,
      hos_planned_threshold: null,
      category_filter: null,
      early_threshold: 10,
      late_threshold: 10,
      use_booking_actuals: false,
      duration_threshold_options: [
        { value: null, text: 'All' },
        { value: '1', text: '> 1m' },
        { value: '10', text: '> 10m' },
        { value: '15', text: '> 15m' },
        { value: '30', text: '> 30m' },
        { value: '45', text: '> 45m' },
        { value: '60', text: '> 1h' },
        { value: '120', text: '> 2h' },
      ],
      loading: false,
      results_cached_at: null,
      polling: false,
      email_opt_in_sent: false,
      details_fromDate: justDate(moment().subtract(1, 'days')),

      options: {
        format: 'MM-DD-YYYY',
        useCurrent: true,
        // a year back. the report takes a single date, so reaching further
        // back never widens a query.
        // caveat for TBN: tbn_process_bookings _expiry_days was 60 until it
        // was raised to 730 on 2026-09-01, so bookings whose trips finished
        // more than ~60 days before that were already reaped and a date back
        // there reads empty until someone re-syncs. actuals survive (their
        // table has TTL disabled) but this report is bookings-driven.
        minDate: moment().subtract(1, 'years'),
        maxDate: moment().add(90, 'days'),
        keepOpen: false,
        showClear: true,
        showClose: true,
      },
    }
  },
  created() {
     this.check_features()
  },
  methods: {
    async check_features() {
    },
    // FI-1432: user opts in to receive an email when this long-running TBN daily report finishes.
    async optInEmailWhenReady() {
      try {
        await tbn_register_email_when_ready({
          tenant: this.user.tenant,
          report_type: 'daily_driver_report',
          params: {
            date: this.details_fromDate,
          },
        })
        this.email_opt_in_sent = true
      } catch (e) {
        this.$emit('showAlert', "couldn't save the notification preference — try again", 'danger', 4000)
      }
    },
    toggle_div(item,field) {
      item[field] = !item[field];
      this.$forceUpdate();
    },
    async load_data(reprocess) {
      if (this._retryTimerId) { clearTimeout(this._retryTimerId); this._retryTimerId = null }
      if (this._pollingTimerId) { clearTimeout(this._pollingTimerId); this._pollingTimerId = null }
      this.loading = true
      this.results_cached_at = null
      if (!this.polling) {
        this._pollingTimerId = setTimeout(() => { this._pollingTimerId = null; this.polling = true }, 30000)
      }
      try {
        const {value: {data = []}} = await get_tbn_actuals_driver_payroll_report(this.user.tenant, moment(this.details_fromDate, 'MM-DD-YYYY').format('YYYY-MM-DD'), reprocess, this.use_booking_actuals, this.polling ? { timeout: 30000, poll: true } : {})
        if (this._pollingTimerId) { clearTimeout(this._pollingTimerId); this._pollingTimerId = null }
        if (data.status === 'processing') {
          this._retryTimerId = setTimeout(() => { this._retryTimerId = null; this.load_data(false) }, 10000)
          return
        }
        this.polling = false
        this.summary = data.summary
        moment.tz.setDefault(this.summary.timezone)
        this.items = data.results
        this.items_full = data.results
        this.results_cached_at = data.results_cached_at || null
        this.loading = false
        this.filter_items()
      } catch (e) {
        if (this._pollingTimerId) { clearTimeout(this._pollingTimerId); this._pollingTimerId = null }
        this.polling = true
        this.loading = false
        this._retryTimerId = setTimeout(() => { this._retryTimerId = null; this.load_data(false) }, 30000)
      }
    },
    async shift_detail_graph_date(shift) {
      this.details_fromDate = moment(this.details_fromDate, 'MM-DD-YYYY').add(shift, 'days').format('MM-DD-YYYY');
    },
    async save_notes(item) {
      try {
        let json = {
          'event_hash': item.event_notes_hash,
          'event_target': item.event_target,
          'notes': item.event_notes,
          'categories': item.event_categories
        }
        const {value: { data = [] } } = await save_event_notes(this.user.tenant, json)
      } catch(e) {
        this.$emit('showAlert', 'error updating data, try logging in again or contact support if needed', 'danger', 5000)
      }
    },
    filter_items() {
      this.items = this.items_full
      if (this.category_filter)
        this.items = this.items.filter(item => item.event_categories && item.event_categories.includes(this.category_filter))
      if (this.booking_filter)
        this.items = this.items.filter(item => (this.contains(item.charterID,this.booking_filter) || this.contains(item.driver,this.booking_filter) || this.contains(item.vehicle,this.booking_filter)))
      if (this.plan_payroll_threshold)
        this.items = this.items.filter(item => (Math.abs(item.plannedMinusPayroll) > this.plan_payroll_threshold))
      if (this.hos_payroll_threshold)
        this.items = this.items.filter(item => (Math.abs(item.hosMinusPayroll) > this.hos_payroll_threshold))
      if (this.hos_planned_threshold)
        this.items = this.items.filter(item => (item.hosMinutes && item.plannedMinutes && Math.abs(item.hosMinusPlanned) > this.hos_planned_threshold))
    },
    contains(val1, val2) {
      if (val1 != null) {
        return val1.toLowerCase().indexOf(val2.toLowerCase()) != -1;
      }
      return false;
    },
    sort_dates(col) {
      console.log("sort_dates:" + col)
      if (this.sort_col == col) {
        this.items = this.items.reverse()
      } else {
        this.items = this.items.sort((a, b) => {
          // console.log("sort:" + a[col] + ", " + b[col])

          if (a[col] && !b[col]) {
            return -1
          } else if (!a[col] && b[col]) {
            return 1
          } else if (!a[col] && !b[col]) {
            return -1
          } else {
            return new Date(a[col]) - new Date(b[col])
          }
        })
      }
      this.sort_col = col
    },
    sort_numbers(col) {
      if (this.sort_col == col) {
        this.items = this.items.reverse()
      } else {
        this.items = this.items.sort((a, b) => {
          if (a[col] && !b[col]) {
            return -1
          } else if (!a[col] && b[col]) {
            return 1
          } else if (!a[col] && !b[col]) {
            return -1
          } else {
            return a[col] - b[col]
          }
        })
      }
      this.sort_col = col
    },
    sort_strings(col) {
      if (this.sort_col == col) {
        this.items = this.items.reverse()
      } else {
        this.items = this.items.sort((a, b) => {
          if (a[col] && !b[col]) {
            return -1
          } else if (!a[col] && b[col]) {
            return 1
          } else if (!a[col] && !b[col]) {
            return -1
          } else {
            let fa = a[col].charAt(0).toLowerCase(),
                fb = b[col].charAt(0).toLowerCase();

            if (fa < fb) {
              return -1;
            }
            if (fa > fb) {
              return 1;
            }
            return 0;
          }
        })
      }
      this.sort_col = col
    },
    async download(){
      this.$emit('showAlert', 'processing download request', 'info')
      axios.defaults.headers.common.Accept = {}
      axios.post(
          `${api_url}tbn/reports/actuals/driverpayroll/download`,
          {
            tenant: this.user.tenant,
            date: moment(this.details_fromDate, 'MM-DD-YYYY').format('YYYY-MM-DD')
          }, {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('jwt')}`,
              Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            },
            responseType: 'blob'
          }
      ).then((response) => {
        axios.defaults.headers.common.Accept = 'application/json'
        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute(
            'download',
            `payroll-daily-${this.details_fromDate}.csv`
        )
        document.body.appendChild(link)
        link.click()
        this.$emit('showAlert', 'download complete', 'success', 5000)
      }).catch(e => {
        axios.defaults.headers.common.Accept = 'application/json'
        this.$emit('showAlert', 'data could not be downloaded.', 'danger')
      })
    },
  },
  computed:{
    ...mapState({
      user: state => state.profile.user
    }),
    formattedCachedAt() {
      if (!this.results_cached_at) return ''
      const m = moment(this.results_cached_at)
      return m.isValid() ? m.format('MMM D, YYYY h:mm A') : this.results_cached_at
    }
  },
  beforeDestroy() {
    if (this._retryTimerId) clearTimeout(this._retryTimerId)
    if (this._pollingTimerId) clearTimeout(this._pollingTimerId)
  },
  watch: {
    user: {
      deep: true,
      handler() {
        this.check_features()
      }
    },
    plan_payroll_threshold(val) {
      this.filter_items()
    },
    hos_payroll_threshold(val) {
      this.filter_items()
    },
    hos_planned_threshold(val) {
      this.filter_items()
    },
    booking_filter() {
      this.filter_items()
    },
    category_filter() {
      this.filter_items()
    },
  }
}
</script>

<style scoped>

thead {
  position: sticky;
  top: 0;
  background-color: #F9F8F8;
  border: 1px solid black;
  z-index: 999;
}

tr:nth-child(even) {
  background-color: #f2f2f2 !important;
}

.arrows {font-size: 22px; font-weight: bold; color: var(--fi-rail-accent) !important }

.early-duration { background-color: hsla(120,100%,75%,0.2)}
.late-duration { background-color: rgba(255,0,0,0.2)}

.early { background-color: hsla(120,100%,75%,0.2)}
.late { background-color: rgba(255,0,0,0.2)}
.ontime { background-color: hsla(200,75%,50%,0.2)}
.private { background-color: hsla(200,40%,80%,0.2) }
.contract { background-color: hsla(250,60%,70%,0.2) }

</style>
